import type { IHomePageSubCategoryWithCoursesHit } from '@/types/search'
import { Typography } from '@mui/material'
import React, { ComponentProps, Fragment } from 'react'
import { useHits, useInstantSearch } from 'react-instantsearch'
import { NewCourseCard } from '../../course/components/NewCourseCard'
import { NoResultsState } from '../../search/components/NoResultsState'
import { SearchErrorState } from '../../search/components/SearchErrorState'
import { SearchLoadingState } from '../../search/components/SearchLoadingState'
import { adaptCourseCollectionToCourseCard } from '../../search/lib/course-adapter'
import {
  AvailableLanguages,
  useLanguageSelection,
} from '../../shared/providers/LanguageSelectionProvider'

type GroupedChildrenResults<L extends AvailableLanguages> = Record<
  string,
  IHomePageSubCategoryWithCoursesHit<L>[]
>

type GroupedCoursesHitsProps = ComponentProps<'div'> & {
  splitGroupsSize: number
}

export function GroupedCoursesHits({
  children,
  splitGroupsSize,
  ...props
}: GroupedCoursesHitsProps) {
  const { currentLocale } = useLanguageSelection()

  const { items = [] } =
    useHits<IHomePageSubCategoryWithCoursesHit<typeof currentLocale>>()

  const groupedItems: GroupedChildrenResults<typeof currentLocale> =
    items.reduce((acc, item) => {
      const groupTitle = item.subcategory.name

      if (acc[groupTitle]) {
        acc[groupTitle].push(item)
      } else {
        acc[groupTitle] = [item]
      }
      return acc
    }, {})

  const { status, refresh, setUiState } = useInstantSearch()

  const isStalled = status === 'stalled'
  const isLoading = status === 'loading'
  const isError = status === 'error'

  const handleErrorRetry = () => {
    refresh()
  }

  const handleNoResultsRetry = () => {
    setUiState({})
  }

  const childrenItems = React.Children.toArray(children)

  return (
    <>
      {isError ? (
        <SearchErrorState
          onRetry={handleErrorRetry}
          className="md:min-h-[65vh]"
        />
      ) : (isLoading || isStalled) && !items.length ? (
        <SearchLoadingState {...props} pageSize={splitGroupsSize} />
      ) : !isLoading && !items.length ? (
        <NoResultsState
          onRetry={handleNoResultsRetry}
          className="md:min-h-[65vh]"
        />
      ) : (
        <div {...props}>
          {Object.entries(groupedItems).map(([title, items], index) => (
            <Fragment key={title}>
              {(index + 1) % splitGroupsSize === 0 &&
                childrenItems[Math.floor(index / splitGroupsSize)]}
              <section>
                <div className="flex justify-between">
                  <Typography variant="h2" className="mb-8">
                    {title}
                  </Typography>
                  {/**
                   * TODO: enable when category page is ready
                  <SecondaryButton
                    href={`/category/${items?.at(0)?.subcategory.slug}`}
                    className="self-start"
                  >
                    {tCommon('view_more')}
                  </SecondaryButton>
                   */}
                </div>
                <ul className="grid grid-flow-row-dense grid-cols-1 gap-4 md:mt-2 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((hit) => (
                    <li key={hit.course.id}>
                      <NewCourseCard
                        course={adaptCourseCollectionToCourseCard<
                          typeof currentLocale
                        >(hit.course, currentLocale)}
                        className="h-full"
                      />
                    </li>
                  ))}
                </ul>
              </section>
            </Fragment>
          ))}
        </div>
      )}
    </>
  )
}
